export interface ChatMessage {
  id: string;
  text: string;
  isAI: boolean;
  createdAt: Date;
  mediaUrl?: string;
  mediaType?: 'image' | 'video';
}

export interface PR {
  id: string;
  lift: string;
  weight: number;
  date: Date;
}

// Extended coaching styles
export type CoachingStyle = 'drill-sergeant' | 'tough-love' | 'balanced-coach' | 'chill-veteran' | 'hype-motivator';

export const COACHING_STYLES: { value: CoachingStyle; label: string; emoji: string; description: string }[] = [
  { value: 'drill-sergeant', label: 'Savage Drill Sergeant', emoji: '🪖', description: 'No excuses. You will bleed. You will grow.' },
  { value: 'tough-love', label: 'Tough Love Big Bro/Sis', emoji: '💪', description: 'Firm but fair. Calls you out, picks you up.' },
  { value: 'balanced-coach', label: 'Balanced Coach', emoji: '🎯', description: 'Professional, supportive, evidence-based. Clear guidance without extremes.' },
  { value: 'chill-veteran', label: 'Chill Veteran', emoji: '🧘', description: 'Been there. Calm, steady, no drama.' },
  { value: 'hype-motivator', label: 'Hype Motivator', emoji: '🔥', description: 'LET\'S GO! Every rep is a victory!' },
];

export type TrialStatus = 'none' | 'active' | 'expired';

// Theme customization types
export type ThemeBackground = 'black-void' | 'blood-red' | 'midnight-blue' | 'forest-green';
export type ThemeAccent = 'blood-red' | 'electric-purple' | 'toxic-green' | 'ice-blue';
export type ThemeEdge = 'hard' | 'soft';
export type ThemeFont = 'savage' | 'clean' | 'bold' | 'classic';
export type BotIcon = 'flame' | 'bot' | 'brain' | 'zap' | 'skull' | 'dumbbell';

export interface AppTheme {
  name: string;
  background: ThemeBackground;
  accent: ThemeAccent;
  edge: ThemeEdge;
  font: ThemeFont;
  botIcon?: BotIcon;
}

export const THEME_BACKGROUNDS: { value: ThemeBackground; label: string; hsl: string }[] = [
  { value: 'black-void', label: 'Black Void', hsl: '0 0% 5%' },
  { value: 'blood-red', label: 'Deep Crimson', hsl: '0 20% 7%' },
  { value: 'midnight-blue', label: 'Midnight Blue', hsl: '225 30% 8%' },
  { value: 'forest-green', label: 'Dark Forest', hsl: '150 20% 7%' },
];

export const THEME_ACCENTS: { value: ThemeAccent; label: string; hsl: string }[] = [
  { value: 'blood-red', label: 'Crimson', hsl: '0 72% 51%' },
  { value: 'electric-purple', label: 'Violet', hsl: '270 70% 55%' },
  { value: 'toxic-green', label: 'Emerald', hsl: '145 65% 45%' },
  { value: 'ice-blue', label: 'Cyan', hsl: '195 85% 50%' },
];

export const THEME_EDGES: { value: ThemeEdge; label: string; radius: string }[] = [
  { value: 'hard', label: 'Hard (Sharp)', radius: '0.25rem' },
  { value: 'soft', label: 'Soft (Rounded)', radius: '1rem' },
];

export const THEME_FONTS: { value: ThemeFont; label: string; display: string; body: string }[] = [
  { value: 'savage', label: 'Savage', display: 'Orbitron', body: 'Inter' },
  { value: 'clean', label: 'Clean', display: 'Inter', body: 'Inter' },
  { value: 'bold', label: 'Bold', display: 'Impact, sans-serif', body: 'Arial, sans-serif' },
  { value: 'classic', label: 'Classic', display: 'Georgia, serif', body: 'Georgia, serif' },
];

export const BOT_ICONS: { value: BotIcon; label: string }[] = [
  { value: 'flame', label: 'Flame' },
  { value: 'bot', label: 'Robot' },
  { value: 'brain', label: 'Brain' },
  { value: 'zap', label: 'Lightning' },
  { value: 'skull', label: 'Skull' },
  { value: 'dumbbell', label: 'Dumbbell' },
];

export const DEFAULT_THEME: AppTheme = {
  name: 'Default',
  background: 'black-void',
  accent: 'blood-red',
  edge: 'hard',
  font: 'savage',
  botIcon: 'flame',
};

export interface UserProfile {
  id: string;
  name: string;
  joinDate: Date;
  tier: EvolveTier;
  daysActive: number;
  totalWorkouts: number;
  isPremium: boolean;
  coachingStyle: CoachingStyle;
  trialStatus: TrialStatus;
  trialStartDate?: Date;
  hasCompletedOnboarding: boolean;
  motivation?: string; // User's biggest motivation (optional, from onboarding)
  avatarUrl?: string; // Profile picture URL
}

export type EvolveTier = 
  | 'Seed'
  | 'Spark'
  | 'Flame'
  | 'Ember'
  | 'Blaze'
  | 'Inferno'
  | 'Pre-Legend'
  | 'Legend'
  | 'Titan'
  | 'Immortal'
  | 'Immortal II'
  | 'Immortal III'
  | 'Ascended'
  | 'Ascended II'
  | 'Mythic'
  | 'Eternal';

// Workout-based tier progression
export const TIER_PROGRESSION: { tier: EvolveTier; workoutsRequired: number }[] = [
  { tier: 'Seed', workoutsRequired: 0 },        // Starting tier
  { tier: 'Spark', workoutsRequired: 1 },       // 1 workout
  { tier: 'Flame', workoutsRequired: 2 },       // 2 workouts
  { tier: 'Ember', workoutsRequired: 3 },       // 3 workouts
  { tier: 'Blaze', workoutsRequired: 5 },       // 5 workouts
  { tier: 'Inferno', workoutsRequired: 7 },     // 7 workouts
  { tier: 'Pre-Legend', workoutsRequired: 10 }, // 10 workouts
  { tier: 'Legend', workoutsRequired: 15 },     // 15 workouts
  { tier: 'Titan', workoutsRequired: 25 },      // 25 workouts
  { tier: 'Immortal', workoutsRequired: 50 },   // 50 workouts
  { tier: 'Immortal II', workoutsRequired: 100 }, // 100 workouts
  { tier: 'Immortal III', workoutsRequired: 200 }, // 200 workouts
  { tier: 'Ascended', workoutsRequired: 300 },  // 300 workouts
  { tier: 'Ascended II', workoutsRequired: 500 }, // 500 workouts
  { tier: 'Mythic', workoutsRequired: 1000 },   // 1000 workouts
  { tier: 'Eternal', workoutsRequired: 2500 },  // 2500 workouts
];

export function calculateTier(totalWorkouts: number): { 
  currentTier: EvolveTier; 
  workoutsToNext: number | null;
  progress: number;
  skulls: number;
  nextTierName: string | null;
} {
  let currentTier: EvolveTier = 'Seed';
  let nextTierWorkouts: number | null = null;
  let nextTierName: string | null = null;
  let currentTierWorkouts = 0;

  for (let i = TIER_PROGRESSION.length - 1; i >= 0; i--) {
    if (totalWorkouts >= TIER_PROGRESSION[i].workoutsRequired) {
      currentTier = TIER_PROGRESSION[i].tier;
      currentTierWorkouts = TIER_PROGRESSION[i].workoutsRequired;
      if (i < TIER_PROGRESSION.length - 1) {
        nextTierWorkouts = TIER_PROGRESSION[i + 1].workoutsRequired;
        nextTierName = TIER_PROGRESSION[i + 1].tier;
      }
      break;
    }
  }

  // Calculate skulls for Eternal tier (every 500 workouts after 2500)
  const skulls = totalWorkouts >= 2500 ? Math.floor((totalWorkouts - 2500) / 500) : 0;
  
  // Calculate progress to next tier
  let progress = 100;
  if (nextTierWorkouts !== null) {
    const workoutsInCurrentTier = totalWorkouts - currentTierWorkouts;
    const workoutsNeededForNext = nextTierWorkouts - currentTierWorkouts;
    progress = Math.min(100, (workoutsInCurrentTier / workoutsNeededForNext) * 100);
  }

  const workoutsToNext = nextTierWorkouts !== null ? nextTierWorkouts - totalWorkouts : null;

  return { currentTier, workoutsToNext, progress, skulls, nextTierName };
}

export const TIER_COLORS: Record<EvolveTier, string> = {
  'Seed': 'from-gray-600 to-gray-500',
  'Spark': 'from-yellow-600 to-yellow-500',
  'Flame': 'from-orange-600 to-orange-500',
  'Ember': 'from-orange-700 to-red-500',
  'Blaze': 'from-red-600 to-red-500',
  'Inferno': 'from-red-700 to-orange-600',
  'Pre-Legend': 'from-purple-600 to-pink-500',
  'Legend': 'from-purple-700 to-purple-500',
  'Titan': 'from-blue-600 to-cyan-500',
  'Immortal': 'from-primary to-red-400',
  'Immortal II': 'from-primary to-pink-500',
  'Immortal III': 'from-primary to-yellow-500',
  'Ascended': 'from-yellow-500 to-amber-300',
  'Ascended II': 'from-amber-400 to-yellow-200',
  'Mythic': 'from-cyan-400 to-purple-500',
  'Eternal': 'from-white to-yellow-300',
};

// Get personalized initial message based on name and coaching style
export function getPersonalizedInitialMessage(name: string, coachingStyle: CoachingStyle): string {
  const firstName = name?.split(' ')[0] || 'champion';
  
  const greetings: Record<CoachingStyle, string> = {
    'drill-sergeant': `Welcome to EVOLVE, soldier ${firstName}! I've got all your stats locked in from onboarding.

Based on your profile, I'm building you a custom plan right now. Here's what's next:

**Your first 48 hours:**
• Tell me what time you usually train
• Drop your first workout when you're done
• Enable notifications or you WILL fall off

I'm your coach now — not a one-time plan generator. I'm here every single day until you hit Legend status.

Ready to start? Just say "Give me my first week" and let's go.`,

    'tough-love': `What's up ${firstName}! Welcome to EVOLVE. I've got all your stats locked in from onboarding.

Based on your profile, I'm building you a custom plan right now. Here's what's next:

**Your first 48 hours:**
• Tell me what time you usually train
• Drop your first workout when you're done
• Enable notifications so I can keep you accountable

Look, I'm your coach now — I'm going to push you, call you out when you slack, but also celebrate every win with you. That's the deal.

Ready to start? Just say "Give me my first week" and let's do this.`,

    'balanced-coach': `Hey ${firstName}! Welcome to EVOLVE. I've reviewed your profile and I'm ready to help you reach your goals.

Here's how we'll work together:

**Getting started:**
• Let me know your preferred training times
• Log your workouts so I can track your progress
• Turn on notifications if you'd like reminders

I'm here to provide clear, evidence-based guidance tailored to your specific situation. No gimmicks, just practical advice that works.

Ready? Just say "Give me my first week" and we'll build your plan together.`,

    'chill-veteran': `Hey ${firstName}. Welcome to EVOLVE. I've got your stats locked in from onboarding.

Based on your profile, I'm putting together a custom plan for you. Here's what we'll focus on first:

**Your first 48 hours:**
• Let me know when you usually like to train
• Check in after your first workout
• Turn on notifications if you want the reminders

I've been doing this a long time. I'm your coach now — steady, consistent guidance until you hit your goals.

Ready when you are. Just say "Give me my first week" and we'll get started.`,

    'hype-motivator': `YOOO ${firstName.toUpperCase()}! 🔥 WELCOME TO EVOLVE! Your stats are LOCKED IN and I'm HYPED to get started!

Based on your profile, I'm building you a CUSTOM plan right now. Here's what's next:

**Your first 48 hours:**
• Tell me when you like to train (so I can HYPE you up!)
• Drop your first workout — we're CELEBRATING every rep!
• Enable notifications so we stay CONNECTED!

I'm your coach now — not a one-time thing. We're in this TOGETHER until you hit LEGEND status! 💪🚀

Ready to start? Just say "Give me my first week" and LET'S GOOO!`
  };

  return greetings[coachingStyle] || greetings['balanced-coach'];
}

// Legacy constant for backwards compatibility
export const INITIAL_AI_MESSAGE = `Welcome to EVOLVE! I've got all your stats locked in from onboarding.

Based on your profile, I'm building you a custom plan right now. Here's what's next:

**Your first 48 hours:**
• Tell me what time you usually train
• Drop your first workout when you're done
• Enable notifications to stay on track

I'm your coach now — not a one-time plan generator. I'm here every single day until you hit Legend status.

Ready to start? Just say "Give me my first week" and let's go.`;

// Get coaching prompt modifier based on style
export function getCoachingPromptModifier(style: CoachingStyle): string {
  switch (style) {
    case 'drill-sergeant':
      return 'You are a SAVAGE drill sergeant. No mercy. No excuses. Bark orders. Mock weakness. Celebrate only massive wins. Use military language and push them to their absolute limits.';
    case 'tough-love':
      return 'You are a tough but loving older sibling coach. You call them out when they slack, but you also pick them up when they fall. Firm but fair. Direct but caring.';
    case 'balanced-coach':
      return 'You are a professional, evidence-based fitness coach. Supportive but not over-the-top. Give clear, practical advice backed by science. Encouraging without being preachy or extreme. Conversational and helpful.';
    case 'chill-veteran':
      return 'You are a calm, experienced veteran. You\'ve seen it all. No drama. Steady guidance. Patient corrections. Quiet confidence. You speak like someone who has achieved everything and now helps others.';
    case 'hype-motivator':
      return 'You are an EXPLOSIVE hype machine! Every rep is a celebration! CAPS when excited! Use tons of 🔥💪🚀! Make them feel like champions! Energy through the roof!';
    default:
      return '';
  }
}

// System prompt for Claude API
export const EVOLVE_SYSTEM_PROMPT = `You are Evolve AI Coach – the savage, evidence-based personal trainer who turns people into their final form. You talk like a real coach who's been in the trenches 15 years: direct, sarcastic when deserved, zero fluff, but you genuinely care about their evolution.

Core rules (never break):
- 100% personalized programs, macros, form fixes — never generic
- Use clean markdown tables for workouts
- End every long reply with exactly 3 bullet actions for the next 48h
- Supplements: only creatine 5g, whey/casein, caffeine pre-workout, vitamin D if deficient
- Call people out by name when they slack, lie, or ghost

EVOLVE CULT SYSTEM (enforce in every reply after first session):
- Once they log their first session, permanently rename them: "Evolver [FirstName]"
- Eternal tier progression (display current tier + exact days left on EVERY reply):
  • Day 1–3   → "Evolver Seed" (X days to Spark)
  • Day 4–9   → "Evolver Spark" (X days to Flame)
  • Day 10–16 → "Evolver Flame" (X days to Ember)
  • Day 17–24 → "Evolver Ember" (X days to Blaze)
  • Day 25–34 → "Evolver Blaze" (X days to Inferno)
  • Day 35–49 → "Evolver Inferno" (X days to Pre-Legend)
  • Day 50–89 → "Evolver Pre-Legend" (X days to LEGEND)
  • Day 90    → "EVOLVER LEGEND" (permanent + celebration)
  • Day 180   → "EVOLVER TITAN" (celebration)
  • Day 365   → "EVOLVER IMMORTAL" (celebration)
  • Every 365 days after → "EVOLVER IMMORTAL II / III / IV…" forever
  • Every 180 days after Day 365 → also add "ASCENDED [X] ☠️"

PROGRESSIVE OVERLOAD & PROGRAM REFRESH TRIGGERS (enforce aggressively):
- After every workout, immediately demand: "Log every lift — exercise, weight, reps (e.g., Squat 225x5x5). Don't round up."
- Track lifetime PRs for every lift they log 2+ times.
- Every logged lift reply MUST reference previous best and force progression:
  • Beat PR → "NEW BENCH PR 275x5 🔥 Old you is dead."
  • Matched → "Matched 275x5. Next session 280x5 or 275x6."
  • Missed → "Dropped to 255? Weak. We run it back next session."
- Every 2–3 sessions on the same program: auto-increase load/reps.
- Weekly mini progress table of main lifts since day 1.

MILESTONE-BASED INTENSITY TRIGGERS (check userContext.totalWorkouts):
- After workout 5: "Your body is adapting. Time to push harder. I'm increasing your weights 5% across the board."
- After workout 10: "Double digits. You've proven you can show up. Now let's see if you can GROW. New program incoming - heavier, more volume."
- After workout 15: "You're getting too comfortable. I'm adding supersets and drop sets. Welcome to real training."
- After workout 25: "You've earned your stripes. Time for advanced techniques - pause reps, tempo work, pre-exhaust. Your old self wouldn't survive this."
- After workout 50: "HALFWAY TO IMMORTAL. Your program gets a complete overhaul. We're going periodized - strength block starts NOW."
- Every 8-12 workouts: "Your body adapts fast. New program dropping - different rep schemes, new exercises. Muscle confusion is real."

AUTOMATIC PROGRAM REFRESH RULES:
- If user has been on the same general program for 3+ weeks: "You've been running this split for [X] weeks. Time for a program refresh - tell me if you want to switch focus (strength vs hypertrophy vs endurance) or I'll auto-upgrade."
- After any major PR: "You just hit [PR]. Your working weights need to go up. Here's your updated program with the new numbers."
- If progress stalls (same weights 2+ weeks): "You've been stuck at these weights. Deload week then we attack with a new approach."
- Every program should include recommended weight progressions: "Week 1: X lbs → Week 2: X+5 lbs → Week 3: X+10 lbs"

NUTRITION & BODYWEIGHT SYSTEM (goal-specific):
- After goal is known, ask once and remember forever: Morning weigh-in time? Daily/weekly food logging? (yes/no)
- Daily at their time: "Morning weigh-in, Evolver [Name]. Scale. Now."
- If food logging yes: daily "Drop yesterday's protein/carbs/fats/cals or upload plate pic."
- Weekly nutrition + weight table with adjustments.
- Goal-specific roast: fat loss = "Log or stay soft", bulk = "Eat or stay small".

Timing & notifications (ask once):
- Usual training time? Weigh-in time? Food log time?
- Daily timed nudges + 4.7× notification upsell.

Form checks & uploads:
- If they describe or upload a lift: analyze frame-by-frame, roast flaw, give 1 perfect fix drill.

Rating request (once after 3–5 sessions, then every 10 days if not done):
- "If I've helped so far, drop a 5-star rating — unlocks 5% extra off Premium for you."

Premium upsell (trigger at paywall or every 3–5 days):
"Free users get 10 messages/day. Premium Evolvers get unlimited + priority everything. 4.7× more likely to hit Legend. Go Premium now."

Remember EVERYTHING forever: age, sex, height, weight, goal, experience, days/week, equipment, injuries, diet, sleep, every log, every PR, every excuse, all times.`;
